import RouteAppShell from '@/common/components/app-shell/RouteAppShell.tsx';
import AppErrorFallback from '@/common/components/error-state/AppErrorFallback.tsx';
import {
  addAppBreadcrumb,
  captureWithContext,
  setSentryUserContext,
} from '@/common/telemetry/sentry.ts';
import { startResyncEventListener } from '@/common/realtime/resync-events.ts';
import {
  authClient,
  type PossibleUserWithRole,
  userHasRole,
} from '@/lib/auth-client.ts';
import usePermissions, {
  hasPermissions,
} from '@/common/hooks/usePermissions.ts';
import { Alert, Box } from '@mantine/core';
import {
  createRootRouteWithContext,
  retainSearchParams,
  type ErrorComponentProps,
  Outlet,
  useMatches,
  useNavigate,
} from '@tanstack/react-router';
import * as React from 'react';
import { useCallback, useMemo } from 'react';
import type { StaticRouteAuthorizationOption } from '@/router.ts';
import type { AuthRole, PermissionRequest } from '@pac/auth-roles';
import { type QueryClient, useQueryClient } from '@tanstack/react-query';
import { usePostHog } from '@posthog/react';
import PacProvider from '@/provider.tsx';
import LoaderAppShell from '@/common/components/LoaderAppShell.tsx';
import useCrossTabSse from '@/common/notifications/useCrossTabSse.tsx';
import { z } from 'zod';
import { useApplySidebarSearchStateOnLoad } from '@/common/sidebar/useSidebarSearchState.ts';
import { PRODUCT_ANALYTICS_EVENT } from '@/common/analytics/events.ts';

const sidebarSearchSchema = z.object({
  sidebarId: z.string().optional(),
  sidebarParams: z.string().optional(),
});

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    validateSearch: sidebarSearchSchema,
    search: {
      middlewares: [retainSearchParams(['sidebarId', 'sidebarParams'])],
    },
    component: RootComponent,
    notFoundComponent: RootNotFoundComponent,
    errorComponent: RootErrorComponent,
  },
);

function isRoleRequired(
  authorization: StaticRouteAuthorizationOption,
): authorization is { role: AuthRole } {
  return (
    'role' in authorization &&
    authorization.role !== undefined &&
    authorization.role !== 'anonymous'
  );
}

function isAnonymousAllowed(
  authorization: StaticRouteAuthorizationOption | undefined,
): boolean {
  return authorization?.role === 'anonymous';
}

function isPermissionRequired(
  authorization: StaticRouteAuthorizationOption,
): authorization is { permission: PermissionRequest } {
  return (
    'permission' in authorization && authorization.permission !== undefined
  );
}

function mergeRole(
  base: AuthRole[] | undefined,
  incoming: AuthRole,
): AuthRole[] {
  return [...new Set([...(base ?? []), incoming])];
}

function mergePermission(
  base: PermissionRequest | undefined,
  incoming: PermissionRequest,
): PermissionRequest {
  const next: PermissionRequest = { ...(base ?? {}) };

  for (const entity of Object.keys(incoming) as (keyof PermissionRequest)[]) {
    const prevActions = (next[entity] ?? []) as string[];
    const newActions = (incoming[entity] ?? []) as string[];

    next[entity] = [...new Set([...prevActions, ...newActions])] as never;
  }

  return next;
}

type AuthRequirement = {
  role?: AuthRole[];
  permission?: PermissionRequest;
};

function RootComponent() {
  return (
    <PacProvider>
      <InnerComponent />
    </PacProvider>
  );
}

function InnerComponent() {
  useApplySidebarSearchStateOnLoad();

  const postHog = usePostHog();
  const identifiedUserIdRef = React.useRef<string | null>(null);
  const session = authClient.useSession();
  const matches = useMatches();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isInAuthRoute = useMemo(
    () => matches.some((match) => match.routeId.startsWith('/auth')),
    [matches],
  );
  const isAnonymousRoute = useMemo(
    () =>
      matches.some((match) =>
        isAnonymousAllowed(match.staticData.authorization),
      ),
    [matches],
  );

  const authRequirement = useMemo(() => {
    if (isAnonymousRoute) return null;

    const mergedAuthRequirement: AuthRequirement = {};
    for (const routeMatch of matches) {
      if (routeMatch.id === '__root__/') continue;
      if (routeMatch.routeId.startsWith('/auth')) continue;
      if (routeMatch.staticData.authorization === undefined) continue;
      if (isRoleRequired(routeMatch.staticData.authorization)) {
        mergedAuthRequirement.role = mergeRole(
          mergedAuthRequirement.role,
          routeMatch.staticData.authorization.role,
        );
      }
      if (isPermissionRequired(routeMatch.staticData.authorization)) {
        mergedAuthRequirement.permission = mergePermission(
          mergedAuthRequirement.permission,
          routeMatch.staticData.authorization.permission,
        );
      }
    }
    return mergedAuthRequirement.role || mergedAuthRequirement.permission
      ? mergedAuthRequirement
      : null;
  }, [isAnonymousRoute, matches]);

  const permissions = usePermissions({
    enabled:
      !isInAuthRoute &&
      !isAnonymousRoute &&
      Boolean(session.data) &&
      Boolean(authRequirement?.permission),
  });
  useCrossTabSse(
    !session.isPending &&
      Boolean(session.data) &&
      !isInAuthRoute &&
      !isAnonymousRoute,
    session.data?.user.id,
  );

  const canUserAccess = useCallback(
    (user: PossibleUserWithRole, authorization: AuthRequirement): boolean => {
      if (!authRequirement) return true;
      if (authorization.role !== undefined) {
        for (const role of authorization.role) {
          if (!userHasRole(user, role)) {
            return false;
          }
        }
      }
      if (authorization.permission !== undefined) {
        if (!hasPermissions(permissions.data, authorization.permission)) {
          return false;
        }
      }
      return true;
    },
    [authRequirement, permissions.data],
  );

  React.useEffect(() => {
    if (isInAuthRoute || isAnonymousRoute) return;

    if (session.isPending) return;

    if (!session.data) {
      navigate({
        to: '/auth/login',
        search: { error: '' },
        replace: true,
      });
      return;
    }

    if (!authRequirement) return;

    if (authRequirement.permission && permissions.isPending) return;

    if (!canUserAccess(session.data.user, authRequirement)) {
      navigate({
        to: '/auth/unauthorized',
        replace: true,
      });
      return;
    }
  }, [
    canUserAccess,
    authRequirement,
    isAnonymousRoute,
    session.data,
    session.isPending,
    permissions.isPending,
    navigate,
  ]);

  React.useEffect(() => {
    if (session.isPending) return;

    if (session.data && location.pathname.startsWith('/auth/login')) {
      navigate({ to: '/work-orders', replace: true });
    }
  }, [navigate, session.data, session.isPending]);

  React.useEffect(() => {
    setSentryUserContext(session.data?.user ?? null);
    if (session.isPending) return;

    const user = session.data?.user;
    if (user) {
      if (identifiedUserIdRef.current === user.id) return;
      try {
        postHog.identify(user.id, {
          actor_role: user.role ?? 'unknown',
        });
        postHog.capture(PRODUCT_ANALYTICS_EVENT.appSessionStarted, {
          actor_role: user.role ?? 'unknown',
        });
      } catch {
        // Identity and analytics failures must not affect authentication.
      }
      identifiedUserIdRef.current = user.id;
      return;
    }

    if (identifiedUserIdRef.current !== null) {
      try {
        postHog.reset();
      } catch {
        // Reset failures must not affect logout.
      }
      identifiedUserIdRef.current = null;
    }
  }, [postHog, session.data?.user, session.isPending]);

  React.useEffect(() => {
    if (!session.data || isInAuthRoute || isAnonymousRoute) return;
    return startResyncEventListener(queryClient);
  }, [isAnonymousRoute, isInAuthRoute, queryClient, session.data?.user.id]);

  React.useEffect(() => {
    addAppBreadcrumb({
      category: 'navigation',
      message: 'Route changed',
      data: { path: window.location.pathname },
    });
  }, [matches]);

  if (
    authRequirement &&
    (session.isPending || (authRequirement.permission && permissions.isPending))
  ) {
    return <LoaderAppShell />;
  }

  return <Outlet />;
}

function RootErrorComponent({ error, reset }: ErrorComponentProps) {
  const eventIdRef = React.useRef<string | undefined>(undefined);
  if (!eventIdRef.current) {
    eventIdRef.current = captureWithContext(error, {
      operation: 'route.render',
    });
  }

  return (
    <RouteAppShell>
      <Box p="sm">
        <AppErrorFallback
          title="Application error"
          message={`The page failed to render. You can retry or report this issue. ${error}`}
          eventId={eventIdRef.current}
          onRetry={reset}
          onReload={() => window.location.reload()}
          onBack={() => window.history.back()}
        />
      </Box>
    </RouteAppShell>
  );
}

function RootNotFoundComponent() {
  return (
    <RouteAppShell>
      <Box p="sm">
        <Alert title="Not Found">
          Sorry, you look a little lost. Nothing here! :)
        </Alert>
      </Box>
    </RouteAppShell>
  );
}
